/**
 * Colour scales of the analysis layers. Sequential only, each with lightness rising monotonically with the value
 * (no rainbow), so a heatmap reads the same in greyscale and for colour-blind viewers:
 * - `height` (viridis stops): height above ground, dark blue at the ground to yellow at the top;
 * - `sun` (inferno stops, its darkest end cut so it stays visible over imagery): sunlight, solar access, TSRF and
 *   panel production, the brighter the sunnier (the convention of Aurora's and Solargraf's irradiance maps).
 * Pure functions: unit tested with node --test.
 */

export type Rgb = [number, number, number];
export type ScaleName = "height" | "sun";

export const SCALES: Record<ScaleName, string[]> = {
  height: ["#440154", "#3b528b", "#21918c", "#5ec962", "#fde725"],
  sun: ["#320a5e", "#781c6d", "#bc3754", "#ed6925", "#fbb61a", "#fcffa4"],
};

export function hexToRgb(hex: string): Rgb {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

const STOPS: Record<ScaleName, Rgb[]> = { height: SCALES.height.map(hexToRgb), sun: SCALES.sun.map(hexToRgb) };

/** The colour at t ∈ [0, 1] (clamped), linear between the stops. */
export function colorAt(scale: ScaleName, t: number): Rgb {
  const stops = STOPS[scale];
  const x = Math.min(1, Math.max(0, Number.isFinite(t) ? t : 0)) * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(x));
  const f = x - i;
  const a = stops[i]!;
  const b = stops[i + 1]!;
  return [Math.round(a[0] + (b[0] - a[0]) * f), Math.round(a[1] + (b[1] - a[1]) * f), Math.round(a[2] + (b[2] - a[2]) * f)];
}

export function rgbCss([r, g, b]: Rgb, alpha = 1): string {
  return alpha >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${alpha})`;
}

/** A value domain: values outside are clamped to its ends. */
export interface Domain {
  min: number;
  max: number;
}

export function normalize(value: number, d: Domain): number {
  return d.max > d.min ? (value - d.min) / (d.max - d.min) : 0.5;
}

/** A robust domain: the given percentiles of the finite values (so one hot pixel does not wash out a map). */
export function robustDomain(values: ArrayLike<number>, lo = 0.02, hi = 0.98): Domain | null {
  const finite: number[] = [];
  for (let i = 0; i < values.length; i++) {
    const v = values[i]!;
    if (Number.isFinite(v)) finite.push(v);
  }
  if (!finite.length) return null;
  finite.sort((a, b) => a - b);
  const at = (q: number) => finite[Math.min(finite.length - 1, Math.max(0, Math.round(q * (finite.length - 1))))]!;
  const d = { min: at(lo), max: at(hi) };
  return d.max > d.min ? d : { min: d.min - 0.5, max: d.max + 0.5 };
}

/** RGBA pixels of a grid of values (row-major, NaN transparent). `alpha` is fixed, or rises from `alpha[0]` at the
 * bottom of the domain to `alpha[1]` at the top (the height map lets the imagery show through on the ground). */
export function paint(values: ArrayLike<number>, scale: ScaleName, domain: Domain, alpha: number | [number, number] = 255): Uint8ClampedArray {
  const out = new Uint8ClampedArray(values.length * 4);
  // a 256-entry lookup: one colour computation per level, not per pixel
  const lut = new Uint8Array(256 * 3);
  for (let i = 0; i < 256; i++) lut.set(colorAt(scale, i / 255), i * 3);
  const span = domain.max - domain.min || 1;
  for (let i = 0; i < values.length; i++) {
    const v = values[i]!;
    if (!Number.isFinite(v)) continue;
    const k = Math.round(Math.min(1, Math.max(0, (v - domain.min) / span)) * 255) * 3;
    out[i * 4] = lut[k]!;
    out[i * 4 + 1] = lut[k + 1]!;
    out[i * 4 + 2] = lut[k + 2]!;
    out[i * 4 + 3] = typeof alpha === "number" ? alpha : Math.round(alpha[0] + ((alpha[1] - alpha[0]) * k) / 765);
  }
  return out;
}

/** Round tick values for a legend: 3-6 "nice" steps (1, 2, 2.5, 5 × 10ⁿ) inside the domain. */
export function niceTicks(d: Domain, target = 5): number[] {
  const span = d.max - d.min;
  if (!(span > 0)) return [d.min];
  const raw = span / Math.max(1, target - 1);
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => span / s <= target) ?? 10 * pow;
  const out: number[] = [];
  for (let k = Math.ceil(d.min / step - 1e-9); k * step <= d.max + 1e-9; k++) out.push(Number((k * step).toPrecision(12)) + 0);
  return out;
}

/** CSS linear-gradient of a scale (legends). */
export function gradientCss(scale: ScaleName, direction = "to right"): string {
  return `linear-gradient(${direction}, ${SCALES[scale].join(", ")})`;
}

/** Contour interval for a height range: about 6-12 lines (0.5, 1, 2 or 5 m). */
export function contourStep(maxHeight: number): number {
  for (const step of [0.5, 1, 2, 5]) if (maxHeight / step <= 12) return step;
  return 10;
}
